import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Divider, Dropdown, Image, List, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { DownloadOutlined, FileImageOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { PatternPointLink } from '../PatternPointLink';
import { MeasurementLink } from '../MeasurementLink';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { formatMeasurementTimestamp, formatUtcTimestamp, measurementDateKey, measurementTimezone } from '../../utils/measurementTime';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number | null;
    patternPointId?: number | null;
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
    onMeasurementClick?: (measurementId: number, areaId?: number | null) => void;
}

type SorterType = 'number' | 'date' | 'text';

interface MeasurementValue {
    valueType: string;
    numValue?: number | null;
    textValue?: string | null;
    processingStage?: string | null;
}

interface MeasurementFile {
    id: number;
    fileName: string;
    fileType?: string;
    fileSize?: number;
    mimeType?: string;
    signedUrl?: string;
    processingStage?: string | null;
}

interface MeasurementText {
    id: number;
    text: string;
    textType?: string;
    createdAt?: string;
    processingStage?: string | null;
}

interface MeasurementItem {
    id: number;
    measurementCode: string;
    createdAt?: string | null;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    areaId?: number | null;
    patternId?: number | null;
    patternPointId?: number | null;
    values?: MeasurementValue[];
    files?: MeasurementFile[];
    texts?: MeasurementText[];
}

const escapeCsvValue = (value: any) => {
    if (value === null || value === undefined) return '';
    const raw = String(value);
    if (raw.includes('"') || raw.includes(',') || raw.includes('\n')) {
        return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
};

const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const extractFilename = (headerValue?: string) => {
    if (!headerValue) return null;
    const match = headerValue.match(/filename=\"?([^\";]+)\"?/i);
    return match?.[1] || null;
};

const CSV_HEADER_LABELS: Record<string, string> = {
    area: 'Area',
    pattern: 'Pattern',
    patternPoint: 'Pattern Point',
    measurementCode: 'Measurement Code',
    measuredAt: 'Measured At',
    createdAt: 'Created At',
    fileCount: 'File Count',
    fileNames: 'File Names',
    filePaths: 'File Paths',
};

const formatValue = (value: any) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value.toFixed(2) : '—';
    }
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const normalizeToString = (value: any) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const buildFilters = (rows: any[], key: string, limit = 50) => {
    const values = new Set<string>();
    rows.forEach((row) => {
        const raw = row?.[key];
        const normalized = normalizeToString(raw);
        if (normalized) values.add(normalized);
    });
    return Array.from(values)
        .slice(0, limit)
        .map((value) => ({ text: value, value }));
};

const buildSorter = (key: string, type: SorterType) => {
    if (type === 'number') {
        return (a: any, b: any) => {
            const av = Number(a?.[key]);
            const bv = Number(b?.[key]);
            const aNum = Number.isFinite(av) ? av : -Infinity;
            const bNum = Number.isFinite(bv) ? bv : -Infinity;
            return aNum - bNum;
        };
    }
    if (type === 'date') {
        return (a: any, b: any) => {
            const aTime = a?.[key] ? new Date(a[key]).getTime() : 0;
            const bTime = b?.[key] ? new Date(b[key]).getTime() : 0;
            return aTime - bTime;
        };
    }
    return (a: any, b: any) => normalizeToString(a?.[key]).localeCompare(normalizeToString(b?.[key]));
};

export const ReportMeasurementList: React.FC<CommonSectionProps> = ({
    section,
    data,
    areas,
    previewAreaId,
    patternPointId,
    onPatternPointClick,
    onMeasurementClick,
}) => {
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(20);
    const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementItem | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<MeasurementFile[]>([]);
    const selectedAreaName = useMemo(
        () => areas.find((area: any) => area?.id === previewAreaId)?.name || null,
        [areas, previewAreaId]
    );

    const filterProcessed = useCallback(
        <T extends { processingStage?: string | null }>(items?: T[]) =>
            (items || []).filter((item) => item.processingStage === 'processed'),
        []
    );

    const { rows, columns, hasContext, csvRows, valueKeys, measurementIds } = useMemo(() => {
        const measurementRows = Array.isArray(data) ? data : [];
        const areaNameMap = new Map<number, string>();
        areas.forEach((area: any) => {
            if (area?.id && area?.name) areaNameMap.set(area.id, area.name);
        });

        const valueTypes = new Set<string>();
        measurementRows.forEach((m: any) => {
            filterProcessed(m.values || []).forEach((v: any) => {
                if (v?.valueType) valueTypes.add(v.valueType);
            });
        });

        const tableRows = measurementRows.map((m: any) => {
            const processedFiles = filterProcessed(m.files || []);
            const zipFolder = m.measurementCode || `measurement-${m.id}`;
            const fileNames = processedFiles.map((file: any) => file?.fileName).filter(Boolean);
            const filePaths = fileNames.map((name: string) => `${zipFolder}/${name}`);

            const row: any = {
                key: m.id,
                measurement: m,
                measurementId: m.id,
                measurementCode: m.measurementCode,
                areaId: m.area?.id || m.areaId || null,
                areaName: m.area?.name || areaNameMap.get(m.areaId) || (m.areaId ? `Area ${m.areaId}` : '—'),
                patternId: m.pattern?.id || m.patternId || null,
                patternName: m.pattern?.name || (m.patternId ? `Pattern ${m.patternId}` : '—'),
                patternPointId: m.patternPoint?.id || m.patternPointId || null,
                patternPointLabel: m.patternPoint?.label
                    || (m.patternPoint?.pointIndex !== undefined ? `Point ${m.patternPoint.pointIndex}` : `Point ${m.patternPointId ?? '—'}`),
                measuredAt: m.measuredAt || null,
                createdAt: m.createdAt || null,
                fileNames,
                filePaths,
                fileCount: processedFiles.length,
            };

            filterProcessed(m.values || []).forEach((v: any) => {
                const key = `value_${v.valueType}`;
                row[key] = v.numValue ?? v.textValue ?? null;
            });

            return row;
        });

        const baseColumns: any[] = [
            {
                title: 'Pattern Point',
                dataIndex: 'patternPointLabel',
                key: 'patternPointLabel',
                sorter: buildSorter('patternPointLabel', 'text'),
                filters: buildFilters(tableRows, 'patternPointLabel'),
                onFilter: (value: any, record: any) => normalizeToString(record.patternPointLabel) === value,
                width: 160,
                render: (value: any, record: any) => {
                    const fallbackAreaId = record.areaId || previewAreaId;
                    if (
                        onPatternPointClick &&
                        record.patternPointId &&
                        record.patternId &&
                        fallbackAreaId
                    ) {
                        return (
                            <span className="report-measurement-list-point-link">
                                <PatternPointLink
                                    patternPointId={record.patternPointId}
                                    patternId={record.patternId}
                                    areaId={fallbackAreaId}
                                    patternPointLabel={value}
                                    onClick={onPatternPointClick}
                                />
                            </span>
                        );
                    }
                    return value || '—';
                },
            },
            {
                title: 'Pattern',
                dataIndex: 'patternName',
                key: 'patternName',
                sorter: buildSorter('patternName', 'text'),
                filters: buildFilters(tableRows, 'patternName'),
                onFilter: (value: any, record: any) => normalizeToString(record.patternName) === value,
                width: 160,
            },
            {
                title: 'Area',
                dataIndex: 'areaName',
                key: 'areaName',
                sorter: buildSorter('areaName', 'text'),
                filters: buildFilters(tableRows, 'areaName'),
                onFilter: (value: any, record: any) => normalizeToString(record.areaName) === value,
                width: 160,
            },
            {
                title: 'Measurement Code',
                dataIndex: 'measurementCode',
                key: 'measurementCode',
                sorter: buildSorter('measurementCode', 'text'),
                filters: buildFilters(tableRows, 'measurementCode'),
                onFilter: (value: any, record: any) => normalizeToString(record.measurementCode) === value,
                width: 180,
                render: (value: any, record: any) => {
                    const measurementAreaId = record.areaId ?? previewAreaId ?? null;
                    const cellStyle: React.CSSProperties = {
                        display: 'inline-block',
                        maxWidth: '100%',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    };
                    if (onMeasurementClick && record.measurementId) {
                        return (
                            <span className="report-measurement-list-report-link">
                                <MeasurementLink
                                    measurementId={record.measurementId}
                                    measurementLabel={value}
                                    areaId={measurementAreaId}
                                    onClick={onMeasurementClick}
                                    style={cellStyle}
                                />
                            </span>
                        );
                    }
                    return (
                        <span style={cellStyle}>
                            {value || '—'}
                        </span>
                    );
                },
            },
            {
                title: 'Measured At',
                dataIndex: 'measuredAt',
                key: 'measuredAt',
                sorter: buildSorter('measuredAt', 'date'),
                defaultSortOrder: 'ascend' as const,
                filters: buildFilters(
                    tableRows.map((row: any) => ({
                        ...row,
                        measuredAt: measurementDateKey(row.measuredAt, row.measurement),
                    })),
                    'measuredAt'
                ),
                onFilter: (value: any, record: any) =>
                    record.measuredAt
                        ? measurementDateKey(record.measuredAt, record.measurement) === value
                        : value === '',
                render: (value: any, record: any) => formatMeasurementTimestamp(value, record.measurement, { seconds: false }),
                width: 180,
            },
            {
                title: 'Uploaded At',
                dataIndex: 'createdAt',
                key: 'createdAt',
                sorter: buildSorter('createdAt', 'date'),
                filters: buildFilters(
                    tableRows.map((row: any) => ({
                        ...row,
                        createdAt: row.createdAt ? new Date(row.createdAt).toISOString().slice(0, 10) : '',
                    })),
                    'createdAt'
                ),
                onFilter: (value: any, record: any) =>
                    record.createdAt
                        ? new Date(record.createdAt).toISOString().slice(0, 10) === value
                        : value === '',
                render: (value: any) => formatUtcTimestamp(value),
                width: 180,
            },
        ];

        const valueColumns = Array.from(valueTypes).map((valueType) => {
            const key = `value_${valueType}`;
            const nonNullValues = tableRows
                .map((row: any) => row[key])
                .filter((value: any) => value !== null && value !== undefined);
            const isNumeric = nonNullValues.length > 0 && nonNullValues.every((value: any) => typeof value === 'number');
            const sorterType: SorterType = isNumeric ? 'number' : 'text';
            return {
                title: valueType,
                dataIndex: key,
                key,
                sorter: buildSorter(key, sorterType),
                filters: buildFilters(tableRows, key),
                onFilter: (value: any, record: any) => normalizeToString(record[key]) === value,
                render: (value: any) => formatValue(value),
                width: 140,
            };
        });

        const columns = [...baseColumns, ...valueColumns];

        const hasPatternPointContext =
            patternPointId !== undefined && patternPointId !== null
                ? true
                : Boolean(section?.patternPointId);
        const hasPatternContext = Boolean(section?.patternId);
        const hasAreaContext = Boolean(previewAreaId);

        const csvData = tableRows.map((row: any) => {
            const formatted: Record<string, any> = {
                measurementId: row.measurementId,
                area: row.areaName,
                pattern: row.patternName,
                patternPoint: row.patternPointLabel,
                measurementCode: row.measurementCode,
                measuredAtUtc: row.measuredAt ? new Date(row.measuredAt).toISOString() : '',
                measuredAtLocal: formatMeasurementTimestamp(row.measuredAt, row.measurement, { includeZone: false }),
                measuredTimezone: measurementTimezone(row.measurement),
                uploadedAtUtc: row.createdAt ? new Date(row.createdAt).toISOString() : '',
                fileCount: row.fileCount || 0,
                fileNames: (row.fileNames || []).join('; '),
                filePaths: (row.filePaths || []).join('; '),
            };
            valueTypes.forEach((valueType) => {
                const key = `value_${valueType}`;
                formatted[valueType] = row[key] ?? '';
            });
            return formatted;
        });

        const csvKeys = [
            'area',
            'pattern',
            'patternPoint',
            'measurementCode',
            'measuredAtUtc',
            'measuredAtLocal',
            'measuredTimezone',
            'uploadedAtUtc',
            'fileCount',
            'fileNames',
            'filePaths',
            ...Array.from(valueTypes),
        ];

        const ids = tableRows.map((row: any) => row.measurementId).filter(Boolean);
        return {
            rows: tableRows,
            columns,
            hasContext: hasPatternPointContext || hasPatternContext || hasAreaContext,
            csvRows: csvData,
            valueKeys: csvKeys,
            measurementIds: ids,
        };
    }, [data, areas, section, patternPointId, previewAreaId, filterProcessed]);

    useEffect(() => {
        if (!measurementIds.length) {
            setSelectedRowKeys([]);
            return;
        }
        setSelectedRowKeys((prev) =>
            prev.filter((key) => measurementIds.includes(Number(key)))
        );
    }, [measurementIds]);

    const selectedMeasurementIds = useMemo(() => {
        return selectedRowKeys
            .map((key) => Number(key))
            .filter((id) => !Number.isNaN(id));
    }, [selectedRowKeys]);

    const measurementMeta = useMemo(() => {
        const parts = [
            selectedAreaName,
            `${rows.length} measurements`,
            `${Math.max(columns.length - 6, 0)} value columns`,
        ].filter(Boolean);
        return parts.join(' · ');
    }, [selectedAreaName, rows.length, columns.length]);

    const selectionMenuItems = useMemo(() => ([
        { key: 'select-page', label: 'Select this page' },
        { key: 'select-all', label: 'Select all results' },
        { key: 'clear', label: 'Clear selection' },
    ]), []);

    const handleSelectionMenu = useCallback((key: string) => {
        if (key === 'select-page') {
            const start = (currentPage - 1) * currentPageSize;
            const end = start + currentPageSize;
            setSelectedRowKeys(rows.slice(start, end).map((row: any) => row.measurementId));
            return;
        }
        if (key === 'select-all') {
            setSelectedRowKeys(rows.map((row: any) => row.measurementId));
            return;
        }
        if (key === 'clear') {
            setSelectedRowKeys([]);
        }
    }, [currentPage, currentPageSize, rows]);

    const selectionHeader = (
        <Dropdown
            menu={{
                items: selectionMenuItems,
                onClick: ({ key }) => handleSelectionMenu(String(key)),
            }}
            trigger={['click']}
        >
            <span className="report-measurement-list-selection-trigger" onClick={(e) => e.preventDefault()}>
                <Checkbox
                    checked={rows.length > 0 && selectedRowKeys.length === rows.length}
                    indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < rows.length}
                    onChange={() => {
                        // no-op: checkbox is only a dropdown trigger
                    }}
                />
            </span>
        </Dropdown>
    );

    const handleDownloadCsv = useCallback((mode: 'all' | 'selected') => {
        const selectedSet = new Set(selectedMeasurementIds);
        const targetRows =
            mode === 'selected'
                ? csvRows.filter((row: any) => selectedSet.has(row.measurementId))
                : csvRows;
        if (!targetRows.length) {
            message.warning('No measurements to export');
            return;
        }
        const header = valueKeys
            .map((key) => escapeCsvValue(CSV_HEADER_LABELS[key] || key))
            .join(',');
        const lines = targetRows.map((row: Record<string, any>) =>
            valueKeys.map((key) => escapeCsvValue(row[key])).join(',')
        );
        const csvContent = [header, ...lines].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const filename =
            mode === 'selected'
                ? `measurements-selected-${dayjs().format('YYYY-MM-DD')}.csv`
                : `measurements-${dayjs().format('YYYY-MM-DD')}.csv`;
        downloadBlob(blob, filename);
    }, [csvRows, selectedMeasurementIds, valueKeys]);

    const handleDownloadZip = useCallback(async (mode: 'all' | 'selected') => {
        const targetIds = mode === 'selected' ? selectedMeasurementIds : measurementIds;
        if (!targetIds.length) {
            message.warning('No measurements to export');
            return;
        }
        setDownloadingZip(true);
        try {
            const res = await axios.post(
                ENDPOINTS.MEASUREMENTS_BATCH_FILES_ZIP,
                { measurementIds: targetIds },
                { withCredentials: true, responseType: 'blob' }
            );
            const filename =
                extractFilename(res.headers?.['content-disposition']) ||
                (mode === 'selected'
                    ? `measurements-selected-${dayjs().format('YYYY-MM-DD')}.zip`
                    : `measurements-export-${dayjs().format('YYYY-MM-DD')}.zip`);
            downloadBlob(res.data, filename);
        } catch (error) {
            console.error('[MeasurementList] Failed to download zip', error);
            message.error('Failed to download measurement files');
        } finally {
            setDownloadingZip(false);
        }
    }, [measurementIds, selectedMeasurementIds]);

    const loadFilesForMeasurement = useCallback(async (measurement: MeasurementItem) => {
        setFilesLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.MEASUREMENT_FILES_BATCH_URLS(measurement.id), {
                withCredentials: true,
            });
            const files: MeasurementFile[] = res.data?.data?.files || [];
            setSelectedFiles(filterProcessed(files));
        } catch (error) {
            console.warn('Unable to load signed URLs, falling back to raw file list', error);
            setSelectedFiles(filterProcessed(measurement.files || []));
        } finally {
            setFilesLoading(false);
        }
    }, [filterProcessed]);

    const handleRowClick = useCallback((measurement: MeasurementItem) => {
        const hydrated = {
            ...measurement,
            values: filterProcessed(measurement.values),
            files: filterProcessed(measurement.files),
            texts: filterProcessed(measurement.texts),
        };
        setSelectedMeasurement(hydrated);
        setModalOpen(true);
        loadFilesForMeasurement(measurement);
    }, [filterProcessed, loadFilesForMeasurement]);

    const imageFiles = useMemo(() => {
        return selectedFiles.filter((file) => (file.mimeType || '').startsWith('image/'));
    }, [selectedFiles]);

    const otherFiles = useMemo(() => {
        return selectedFiles.filter((file) => !(file.mimeType || '').startsWith('image/'));
    }, [selectedFiles]);

    const hasImages = imageFiles.length > 0;
    const hasOtherFiles = otherFiles.length > 0;

    if (!hasContext) {
        return (
            <Alert
                message="Select an area, pattern, or pattern point to view measurements"
                type="info"
                showIcon
            />
        );
    }

    return (
        <>
            <div className="report-measurement-workspace">
                <div className="report-measurement-head">
                    <div className="report-measurement-title">{section.title || 'Measurements'}</div>
                    <div className="report-measurement-meta">{measurementMeta}</div>
                </div>

                <div className="report-measurement-toolbar">
                    <div className="report-measurement-summary">
                        <span className="report-measurement-pill">{rows.length} measurements</span>
                        <span className="report-measurement-pill report-measurement-pill--muted">
                            {selectedMeasurementIds.length > 0
                                ? `${selectedMeasurementIds.length} selected`
                                : 'Select rows to export'}
                        </span>
                    </div>

                    <Space wrap className="report-measurement-list-actions">
                        <Button
                            className="report-measurement-list-download-csv-button"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadCsv('selected')}
                            disabled={selectedMeasurementIds.length === 0}
                        >
                            Download selected CSV
                        </Button>
                        <Button
                            className="report-measurement-list-download-zip-button"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadZip('selected')}
                            loading={downloadingZip}
                            disabled={selectedMeasurementIds.length === 0}
                        >
                            Download selected files ZIP
                        </Button>
                    </Space>
                </div>

                <Table
                    dataSource={rows}
                    columns={columns}
                    className="report-measurement-list-table"
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: currentPage,
                        pageSize: currentPageSize,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setCurrentPage(page);
                            if (pageSize) {
                                setCurrentPageSize(pageSize);
                            }
                        },
                    }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        preserveSelectedRowKeys: true,
                        columnTitle: selectionHeader,
                        renderCell: (_checked, _record, _index, originNode) => (
                            <span className="report-measurement-list-row-checkbox">{originNode}</span>
                        ),
                    }}
                    onRow={(record: any) => ({
                        onClick: (event) => {
                            const target = event.target as HTMLElement;
                            if (target.closest('.ant-checkbox-wrapper') || target.closest('a')) {
                                return;
                            }
                            if (record?.measurement) {
                                handleRowClick(record.measurement);
                            }
                        },
                        style: { cursor: 'pointer' },
                    })}
                    locale={{
                        emptyText: (
                            <Text type="secondary">No measurements found for this selection.</Text>
                        ),
                    }}
                />
            </div>
            <Modal
                title={selectedMeasurement ? `Measurement ${selectedMeasurement.measurementCode}` : 'Measurement'}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setSelectedMeasurement(null);
                    setSelectedFiles([]);
                }}
                footer={[
                    <Button key="close" onClick={() => setModalOpen(false)}>Close</Button>,
                ]}
                width={900}
            >
                {!selectedMeasurement ? (
                    <Text type="secondary">No measurement selected.</Text>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Values</Text>
                            {selectedMeasurement.values && selectedMeasurement.values.length > 0 ? (
                                <List
                                    size="small"
                                    style={{ marginTop: 4 }}
                                    dataSource={selectedMeasurement.values}
                                    renderItem={(v) => (
                                        <List.Item>
                                            <Space split={<Divider type="vertical" />}>
                                                <Text>{v.valueType}</Text>
                                                <Text strong>{v.textValue ?? v.numValue ?? '—'}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Text type="secondary">No values</Text>
                            )}
                        </Space>

                        <Divider style={{ margin: '4px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Notes</Text>
                            {selectedMeasurement.texts && selectedMeasurement.texts.length > 0 ? (
                                <List
                                    size="small"
                                    style={{ marginTop: 4 }}
                                    dataSource={selectedMeasurement.texts}
                                    renderItem={(t) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={(
                                                    <Space split={<Divider type="vertical" />} size="small">
                                                        <Tag color="blue">{t.textType || 'comment'}</Tag>
                                                        <Text type="secondary">
                                                            {t.createdAt ? dayjs(t.createdAt).format('YYYY-MM-DD HH:mm') : '—'}
                                                        </Text>
                                                    </Space>
                                                )}
                                                description={<Text>{t.text || '—'}</Text>}
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Text type="secondary">No notes</Text>
                            )}
                        </Space>

                        <Divider style={{ margin: '4px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Files & Images</Text>
                            {filesLoading ? (
                                <Spin />
                            ) : hasImages || hasOtherFiles ? (
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {hasImages && (
                                        <div>
                                            <Text type="secondary">Images</Text>
                                            <div style={{ marginTop: 8 }}>
                                                <Space size="middle" wrap>
                                                    {imageFiles.map((file) => (
                                                        <div key={file.id} style={{ width: 160 }}>
                                                            <Image
                                                                src={file.signedUrl || ''}
                                                                alt={file.fileName}
                                                                width="100%"
                                                                style={{ borderRadius: 6, border: '1px solid #f0f0f0' }}
                                                            />
                                                            <div style={{ marginTop: 4, fontSize: 12 }}>
                                                                <FileImageOutlined /> {file.fileName}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </Space>
                                            </div>
                                        </div>
                                    )}

                                    {hasOtherFiles && (
                                        <div>
                                            <Text type="secondary">Other files</Text>
                                            <List
                                                size="small"
                                                style={{ marginTop: 4 }}
                                                dataSource={otherFiles}
                                                renderItem={(file) => (
                                                    <List.Item>
                                                        <Text>{file.fileName}</Text>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    )}
                                </Space>
                            ) : (
                                <Text type="secondary">No files or images</Text>
                            )}
                        </Space>
                    </Space>
                )}
            </Modal>
        </>
    );
};

export default ReportMeasurementList;
